/* eslint-disable camelcase */
import axios from 'axios'
import {
  axiosClient,
  getCSRFToken,
  testRequiredOptions,
} from 'www/utils'
import LocationParser from 'www/modules/LocationParser'
import { REQUEST_ARRAY_NAME } from 'www/modules/Timetable/constants'
import getUserLanguage from 'src/utils/getUserLanguage'
import { getConcatOptionGET } from 'www/vue_components/common/Analyses/functions/getConcatOptionGET'

const locationParserHref = new LocationParser()
const userLanguage = getUserLanguage()

class AppointmentModules {
  /**
   * @description
   *
   * Получает ячейки с записью на прием на страницах услуг
   * Производит проверку обязательных полей данных запроса
   * Ниже пример данных для корректного запроса
   *
   * @example
   *
   * const request = {
   *     days: 14,
   *     day_start: "2021-04-01",
   *     only_free: true,
   *     town_timedelta: "5",
   *     services: [
   *         {
   *             device_id: 180,
   *             service_id: 160,
   *             lpu_id: 37347,
   *         }, {
   *             device_id: 180,
   *             service_id: 180,
   *             lpu_id: 37348,
   *             additional_service_ids: [209, 245, 231],
   *         },
   *     ],
   * };
   *
   * @param {object} options -
   * @param {string | number} options.days - количество дней
   * @param {boolean} options.only_free - присылать все слоты с МИС или только свободные ячейки
   * @param {string} options.day_start - дата с которой будут запрашиваться слоты
   * @param {string} options.town_timedelta - временная разница, поле необходимо для МИС, чтобы выбрать слоты правильно, учитывая разницу во времени
   *
   * @param { Array } options.services - массив с услугами
   *
   * @param {object} options.services.item - объект с данными для конкретного LPU
   * @param {string | number} options.services.lpu_id - id LPU
   * @param {string | number} options.services.device_id - id оборудования
   * @param {string | number} options.services.service_id - id услуги(синонима)
   * @param { Array } options.services.additional_service_ids - дополнительные данные(например МРТ с контрастом или без)
   *
   * @param {string | number} options.devices.lpu_id - id LPU
   * @param { Array } options.devices.devices_ids - список id всего оборудования, которое есть в клинике
   *
   * @returns { Promise } -
   */
  getAppointmentServicesSlots(options) {
    return new Promise((resolve, reject) => {
      const {
        days,
        day_start,
        only_free,
        town_timedelta,
      } = options

      const requestFieldName = 'service_id'
      const requestPartPath = '/ajax/diagnostika/services/schedule/'

      const noticeOptions = {
        mode: 'throw',
        module: 'AppointmentModules',
        method: 'getAppointmentServicesSlots',
      }

      const testBaseOptions = testRequiredOptions({
        ...noticeOptions,
        requiredOptions: {
          days,
          day_start,
          only_free,
          town_timedelta,
        },
      })

      if (!testBaseOptions) {
        return
      }

      if (!options[REQUEST_ARRAY_NAME].length) {
        return
      }

      for (const item of options[REQUEST_ARRAY_NAME]) { // eslint-disable-line no-restricted-syntax
        const testInnerOptions = testRequiredOptions({
          ...noticeOptions,
          requiredOptions: {
            lpu_id: item.lpu_id,
          },
        })

        const requestFieldType = typeof item[requestFieldName]

        if (!testInnerOptions || (requestFieldType !== 'string' && requestFieldType !== 'number')) {
          return
        }
      }

      axios.post(window.location.origin + requestPartPath, options)
        .then(response => resolve(response.data))
        .catch(reject)
    })
  }

  /**
   * Получить ячейки с доступной записью на прием
   *
   * @param {object} opt -
   * @param {any} opt.days - количество дней
   * @param {any} opt.dt_start - дата с которой будут запрашиваться слоты
   * @param {any} opt.doctors_lpus - массив с объектами id врача и id клиники
   * @param {any} opt.town_timedelta - разница во времени относительно сервера
   * @param {any} opt.user_timedelta - разница во времени относительно пользователя
   * @param {boolean} opt.all_slots - присылать ли занятые слоты
   * @param {Array<Array<number>>} opt.lpu_timedelta - если возможно много мест работы в разных городах,
   * то для каждого своя timedelta
   * @param {any} opt.domain - домен Продокторов
   * @returns {Promise} -
   */
  getAppointmentDoctorsSlots({
    days, dt_start, doctors_lpus, town_timedelta, user_timedelta, all_slots, lpu_timedelta, domain,
  }) {
    return new Promise((resolve, reject) => {
      if (!days) {
        reject(new Error('Не передано количество дней'))
      }

      if (!dt_start) {
        reject(new Error('Не передана стартовая дата'))
      }

      if (!doctors_lpus.length) {
        reject(new Error('Не передан doctors_lpus'))
      }

      if (!(town_timedelta || user_timedelta) && !lpu_timedelta) {
        reject(new Error('Не передан timedelta'))
      }

      if ((town_timedelta && lpu_timedelta) || (town_timedelta && user_timedelta)) {
        reject(new Error('Передано несколько timedelta'))
      }

      const requestData = {
        days, dt_start, doctors_lpus, town_timedelta, all_slots, lpu_timedelta, user_timedelta,
      }

      let host = '/'

      if (domain) {
        host = `//${domain}/`
      }

      $.ajax({
        url: `${host}ajax/schedule/slots_bulk/`,
        type: 'POST',
        data: JSON.stringify(requestData),
        async: true,
        crossDomain: true,
        processData: false,
        headers: {
          'content-type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        success(res) {
          resolve(res)
        },
      })
    })
  }

  /**
   * Получить ячейки с доступной записью на прием
   *
   * @param {object} opts -
   * @param {any} opts.days - количество дней
   * @param {any} opts.dt_start - дата с которой будут запрашиваться слоты
   * то для каждого своя timedelta
   * @returns {Promise} -
   */

  getAppointmentLpusSlots({
    days, dt_start, lpus_specialities, town_timedelta,
  }) {
    return new Promise((resolve, reject) => {
      if (!days) {
        reject(new Error('Не передано количество дней'))
      }

      if (!dt_start) {
        reject(new Error('Не передана стартовая дата'))
      }

      if (!lpus_specialities.length) {
        reject(new Error('Не передан lpus_specialities'))
      }

      const data = {
        days, dt_start, lpus_specialities, town_timedelta,
      }

      axiosClient.post('/ajax/schedule/slots_bulk_by_specialities/', data)
        .then(res => resolve(res.data))
        .catch(err => reject(err))
    })
  }

  /**
   * Переходит на страницу записи
   *
   * @param {object} opt -
   * @param {any} opt.doctor - ID врача
   * @param {any} opt.lpu - ID клиники
   * @param {any} opt.slot - дата записи
   * @param {any} opt.filter - дополнительная фильтрация
   * @param {any} opt.type - тип приема (клиника, на дом, онлайн)
   * @param {any} opt.spec_placement - Переход с карточки спец. размещения (скорее всего, легаси)
   * @param {any} opt.doctorSpec - ID специальности врача
   * @param {any} opt.domain - домен (например, prodoctorov.ru)
   */
  redirectToNewAppointment({
    doctor, lpu, slot, filter, type, spec_placement, doctorSpec, domain,
  }) {
    let restParams = ''
    let host = `${window.location.origin}/`

    if (slot) {
      restParams += `&slot=${slot}`
    }

    if (type && type !== 'default') {
      restParams += `&type=${type}`
    }

    if (spec_placement) {
      restParams += `&spec_placement=${spec_placement}`
    }

    if (lpu) {
      restParams += `&lpu=${lpu}`
    }

    if (doctorSpec) {
      restParams += `&speciality=${doctorSpec}`
    }

    if (filter) {
      restParams += `${filter}`
    }

    if (domain) {
      host = `//${domain}/`
    }

    if (userLanguage !== window.LANGUAGE_DATA.defaultLanguageIso) {
      host += `${userLanguage}/`
    }

    window.location.href = `${host}appointment/vrach/new/?doctor=${doctor}&source=${encodeURIComponent(locationParserHref.source)}${restParams}`
  }

  redirectToNewAppointmentClinic(options = {}) {
    const {
      lpu,
      slot,
      targetDoctorId,
      doctorId,
      equipmentId,
      dTypeId,
      dClassId,
      deviceId,
      synonymId,
      misServiceId, // id - услуги, загружаемой из МИС
      byReferral,
    } = options

    let dParam = ''

    dParam += `&source=${encodeURIComponent(locationParserHref.source)}`

    if (dTypeId) {
      dParam += `&dtype=${dTypeId}`
    } else if (dClassId) {
      dParam += `&dclass=${dClassId}`
    }

    if (deviceId) {
      dParam += `&device=${deviceId}`
    }

    if (synonymId) {
      dParam += `&service=${synonymId}`
    }

    if (misServiceId) {
      dParam += `&service=${misServiceId}`
    }

    if (slot) {
      if (!synonymId) {
        return
      }

      dParam += `&slot=${slot}`
    }

    if (doctorId) {
      dParam += `&doctorId=${doctorId}`
    } else if (targetDoctorId) {
      dParam += `&targetDoctorId=${targetDoctorId}`
    }

    if (equipmentId !== undefined) {
      dParam += `&equipmentId=${equipmentId}`
    }

    if (String(byReferral) === 'true') {
      dParam += '&by_referral=true'
    }

    let { origin } = window.location

    if (userLanguage !== window.LANGUAGE_DATA.defaultLanguageIso) {
      origin += `/${userLanguage}`
    }

    window.location.href = `${origin}/appointment/uslugi/new/?lpu=${lpu}${dParam}`
  }

  redirectToNewAnalyses(payload = {}) {
    let { origin } = window.location

    if (userLanguage !== window.LANGUAGE_DATA.defaultLanguageIso) {
      origin += `/${userLanguage}`
    }

    window.location.assign(
      getConcatOptionGET({
        path: `${origin}/appointment/analyses/new/`,
        payload,
      }),
    )
  }
}

export { AppointmentModules }
export default AppointmentModules
